import type { DeliverableType, Lead, Project } from '../types';
import { formatCurrencyValue, type Language } from './formatUtils';

export type MessageVariableGroup = 'lead' | 'me' | 'project' | 'other';

export interface MessageVariableDef {
  token: string;
  labelKey: string;
  group: MessageVariableGroup;
}

/** Tokens available in templates / compose. */
export const MESSAGE_VARIABLES: MessageVariableDef[] = [
  { token: '{lead_name}', labelKey: 'messages.var.leadName', group: 'lead' },
  { token: '{lead_first_name}', labelKey: 'messages.var.leadFirstName', group: 'lead' },
  { token: '{lead_contact}', labelKey: 'messages.var.leadContact', group: 'lead' },
  { token: '{lead_email}', labelKey: 'messages.var.leadContact', group: 'lead' }, // legacy alias
  { token: '{lead_sector}', labelKey: 'messages.var.leadSector', group: 'lead' },
  { token: '{my_name}', labelKey: 'messages.var.myName', group: 'me' },
  { token: '{my_company}', labelKey: 'messages.var.myCompany', group: 'me' },
  { token: '{project_name}', labelKey: 'messages.var.projectName', group: 'project' },
  { token: '{project_type}', labelKey: 'messages.var.projectType', group: 'project' },
  { token: '{project_amount}', labelKey: 'messages.var.projectAmount', group: 'project' },
  { token: '{project_deadline}', labelKey: 'messages.var.projectDeadline', group: 'project' },
  { token: '{project_deliverables}', labelKey: 'messages.var.projectDeliverables', group: 'project' },
  { token: '{rights_platforms}', labelKey: 'messages.var.rightsPlatforms', group: 'project' },
  { token: '{rights_duration}', labelKey: 'messages.var.rightsDuration', group: 'project' },
  { token: '{rights_territory}', labelKey: 'messages.var.rightsTerritory', group: 'project' },
  { token: '{rights_exclusivity}', labelKey: 'messages.var.rightsExclusivity', group: 'project' },
  { token: '{rights_start}', labelKey: 'messages.var.rightsStart', group: 'project' },
  { token: '{rights_end}', labelKey: 'messages.var.rightsEnd', group: 'project' },
  { token: '{today}', labelKey: 'messages.var.today', group: 'other' },
  { token: '{date}', labelKey: 'messages.var.today', group: 'other' }, // alias
  { token: '{tomorrow}', labelKey: 'messages.var.tomorrow', group: 'other' },
  { token: '{in_1_week}', labelKey: 'messages.var.in1Week', group: 'other' },
  { token: '{in_1_month}', labelKey: 'messages.var.in1Month', group: 'other' },
];

/** Chips shown in UI (hide legacy aliases). */
export const MESSAGE_VARIABLE_CHIPS: MessageVariableDef[] = MESSAGE_VARIABLES.filter(
  (v) => v.token !== '{lead_email}' && v.token !== '{date}'
);

const PROJECT_TYPE_LABELS: Record<DeliverableType, Record<Language, string>> = {
  short_video: { fr: 'Vidéo courte', en: 'Short Video' },
  photo: { fr: 'Photo', en: 'Photo' },
  voice_over: { fr: 'Voice Over', en: 'Voice Over' },
  story: { fr: 'Story', en: 'Story' },
  post: { fr: 'Post / Carrousel', en: 'Post / Carousel' },
  long_video: { fr: 'Vidéo longue', en: 'Long Video' },
  other: { fr: 'Autre', en: 'Other' },
};

export function insertTextAtCursor(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  text: string,
  value: string,
  onChange: (next: string) => void
): void {
  if (!el) {
    onChange(`${value}${text}`);
    return;
  }
  const start = el.selectionStart ?? value.length;
  const end = el.selectionEnd ?? value.length;
  const next = value.slice(0, start) + text + value.slice(end);
  onChange(next);
  requestAnimationFrame(() => {
    el.focus();
    const pos = start + text.length;
    try {
      el.setSelectionRange(pos, pos);
    } catch {
      /* input type may not support selection */
    }
  });
}

function formatShortDate(date: Date | null | undefined, language: Language): string {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat(language === 'fr' ? 'fr-FR' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date instanceof Date ? date : new Date(date));
  } catch {
    return '';
  }
}

/** Local calendar date + offsets (avoids UTC day-shift surprises). */
function addCalendarDays(base: Date, days: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

function addCalendarMonths(base: Date, months: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setMonth(d.getMonth() + months);
  return d;
}

function formatRightsDuration(days: number | undefined, language: Language): string {
  if (days == null || !Number.isFinite(days) || days <= 0) return '';
  if (days % 30 === 0) {
    const months = days / 30;
    if (language === 'fr') {
      return months === 1 ? '1 mois' : `${months} mois`;
    }
    return months === 1 ? '1 month' : `${months} months`;
  }
  if (language === 'fr') {
    return days === 1 ? '1 jour' : `${days} jours`;
  }
  return days === 1 ? '1 day' : `${days} days`;
}

function leadFirstName(name: string | undefined): string {
  if (!name) return '';
  const first = name.trim().split(/\s+/)[0];
  return first || '';
}

function projectTypeLabel(type: DeliverableType | undefined, language: Language): string {
  if (!type) return '';
  return PROJECT_TYPE_LABELS[type]?.[language] || type;
}

function exclusivityLabel(exclusive: boolean | undefined, language: Language): string {
  if (exclusive == null) return '';
  if (language === 'fr') return exclusive ? 'Oui' : 'Non';
  return exclusive ? 'Yes' : 'No';
}

export interface PersonalizeContext {
  lead?: Lead | null;
  project?: Project | null;
  myName?: string;
  myCompany?: string;
  sectorLabel?: string;
  language?: Language;
  /** Override "now" for tests; defaults to current date. */
  now?: Date;
}

/** Replace template tokens with lead / optional project / profile values. */
export function personalizeMessageText(text: string, ctx: PersonalizeContext): string {
  const language = ctx.language ?? 'fr';
  const lead = ctx.lead ?? null;
  const project = ctx.project ?? null;
  const rights = project?.rights ?? null;
  const now = ctx.now ?? new Date();
  const today = formatShortDate(now, language);
  const tomorrow = formatShortDate(addCalendarDays(now, 1), language);
  const in1Week = formatShortDate(addCalendarDays(now, 7), language);
  const in1Month = formatShortDate(addCalendarMonths(now, 1), language);

  let out = text
    .replace(/\{lead_name\}/g, lead?.name || '')
    .replace(/\{lead_first_name\}/g, leadFirstName(lead?.name))
    .replace(/\{lead_email\}/g, lead?.contact_value || '')
    .replace(/\{lead_contact\}/g, lead?.contact_value || '')
    .replace(/\{lead_company\}/g, lead?.name || '') // legacy: same as lead name
    .replace(/\{lead_sector\}/g, ctx.sectorLabel || '')
    .replace(/\{my_name\}/g, ctx.myName || '')
    .replace(/\{my_company\}/g, ctx.myCompany || '')
    .replace(/\{today\}/g, today)
    .replace(/\{date\}/g, today)
    .replace(/\{tomorrow\}/g, tomorrow)
    .replace(/\{in_1_week\}/g, in1Week)
    .replace(/\{in_1_month\}/g, in1Month);

  const amount =
    project != null
      ? formatCurrencyValue(project.amount || 0, language, project.currency || 'EUR')
      : '';

  const deliverables =
    project?.deliverables?.filter(Boolean).join(', ') || '';

  out = out
    .replace(/\{project_name\}/g, project?.name || '')
    .replace(/\{project_type\}/g, projectTypeLabel(project?.type, language))
    .replace(/\{project_amount\}/g, amount)
    .replace(/\{project_deadline\}/g, formatShortDate(project?.deadline, language))
    .replace(/\{project_deliverables\}/g, deliverables)
    .replace(/\{rights_platforms\}/g, rights?.platforms?.length ? rights.platforms.join(', ') : '')
    .replace(/\{rights_duration\}/g, formatRightsDuration(rights?.duration, language))
    .replace(/\{rights_territory\}/g, rights?.territory || '')
    .replace(
      /\{rights_exclusivity\}/g,
      rights ? exclusivityLabel(rights.exclusivity, language) : ''
    )
    .replace(/\{rights_start\}/g, formatShortDate(rights?.startDate ?? null, language))
    .replace(/\{rights_end\}/g, formatShortDate(rights?.endDate ?? null, language));

  return out;
}

/** Append a plain-text email signature outside the compose body (send-time only). */
export function appendEmailSignature(body: string, signature: string | null | undefined): string {
  const trimmedBody = (body || '').replace(/\s+$/, '');
  const trimmedSig = (signature || '').trim();
  if (!trimmedSig) return trimmedBody;
  if (!trimmedBody) return trimmedSig;
  return `${trimmedBody}\n\n--\n${trimmedSig}`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const GMAIL_LINK_STYLE = 'color:#1155cc;text-decoration:underline';

/** Autolink URLs / emails the way Gmail compose tends to. */
function linkifyPlainText(text: string): string {
  const pattern =
    /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  let out = '';
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    out += escapeHtml(text.slice(last, match.index));
    const raw = match[1];
    const trailing = raw.match(/[.,;:!?)]+$/);
    const core = trailing ? raw.slice(0, raw.length - trailing[0].length) : raw;
    const suffix = trailing ? trailing[0] : '';
    let href = core;
    if (core.includes('@') && !/^https?:\/\//i.test(core) && !/^www\./i.test(core)) {
      href = `mailto:${core}`;
    } else if (/^www\./i.test(core)) {
      href = `http://${core}`;
    }
    out += `<a href="${escapeHtml(href)}" style="${GMAIL_LINK_STYLE}" target="_blank">${escapeHtml(core)}</a>`;
    out += escapeHtml(suffix);
    last = match.index + raw.length;
  }
  out += escapeHtml(text.slice(last));
  return out;
}

/** Convert plain compose text to HTML the way Gmail does for Sans Serif + Normal. */
export function plainTextToEmailHtml(text: string): string {
  const trimmed = (text || '').replace(/\s+$/, '');
  if (!trimmed) return '';
  const inner = linkifyPlainText(trimmed).replace(/\r\n|\r|\n/g, '<br>');
  return `<div dir="ltr" style="font-family:Arial,Helvetica,sans-serif;font-size:14.666666666666666px;color:#000000">${inner}</div>`;
}

export type BuildEmailBodiesParams = {
  body: string;
  signatureText?: string | null;
  signatureHtml?: string | null;
  includeSignature?: boolean;
};

export type BuiltEmailBodies = {
  bodyText: string;
  bodyHtml: string | null;
};

/**
 * Build plain + optional HTML bodies for Gmail send.
 * Body = Gmail Normal; HTML signature appended as-is (caller should normalize images).
 */
export function buildEmailBodies(params: BuildEmailBodiesParams): BuiltEmailBodies {
  const include = params.includeSignature !== false;
  const sigHtml = include ? (params.signatureHtml || '').trim() : '';
  const sigText = include ? (params.signatureText || '').trim() : '';

  const bodyText = appendEmailSignature(params.body, sigText || (sigHtml ? htmlToPlainFallback(sigHtml) : ''));

  if (!sigHtml) {
    return { bodyText, bodyHtml: null };
  }

  const bodyHtmlPart = plainTextToEmailHtml(params.body);
  const separator = '<br><br>';
  const bodyHtml = bodyHtmlPart ? `${bodyHtmlPart}${separator}${sigHtml}` : sigHtml;

  return { bodyText, bodyHtml };
}

/** Lightweight tag strip for send-time plain fallback (no DOM required). */
function htmlToPlainFallback(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
